export interface GameEmbed {
  url: string;
  engine: string;
  downloadMb: number;
  studio: string;
}

// The browse axis shown in the sidebar, filter chips and rails. Distinct from
// `gameGenre`, which is a one-per-game descriptive label and too fine-grained
// to filter on.
export const GAME_CATEGORIES = [
  'Action',
  'Racing',
  'Puzzle',
  'Casual',
  'Sports',
  '2-Player',
  '.io',
  'Music',
  'Adventure',
] as const;

export type GameCategory = (typeof GAME_CATEGORIES)[number];

export interface GameMeta {
  slug: string;
  name: string;
  amharic: string;
  tagline: string;
  howTo: string;
  controls: string;
  accent: string;
  // Ordered: the first entry is the game's primary home and the one shown on
  // its card.
  categories: [GameCategory, ...GameCategory[]];
  embed?: GameEmbed;
}

export const GAMES: GameMeta[] = [
  {
    slug: 'gursha',
    name: 'Gursha',
    amharic: 'ጉርሻ',
    tagline: 'Feed a friend, the Ethiopian way.',
    howTo: 'A morsel of injera swings past the mouth. Release it inside the glowing band. Tighter timing makes a bigger gursha. Three misses and the meal is over.',
    controls: 'Tap / Space to feed',
    accent: '#FF6746',
    categories: ['Casual'],
  },
  {
    slug: 'piassa-drift',
    name: 'Piassa Drift',
    amharic: 'ፒያሳ',
    tagline: 'Rush hour on the old boulevard.',
    howTo: 'Weave through Piassa traffic as the street speeds up. Blue taxis cost you a life, golden coffee cups are worth points. Survive as long as you can.',
    controls: '← → or A/D to steer · drag on touch',
    accent: '#F6B73C',
    categories: ['Racing', 'Action'],
  },
  {
    slug: 'lalibela-carver',
    name: 'Lalibela Carver',
    amharic: 'ላሊበላ',
    tagline: 'Cut a church from living rock.',
    howTo: 'Marked blocks glow on the rock face, each with a ring that closes as the stone sets. Strike while the ring is still open. Tighter cuts score better.',
    controls: 'Click / tap the glowing blocks',
    accent: '#D77950',
    categories: ['Puzzle', 'Casual'],
  },
  {
    slug: 'tizita-echoes',
    name: 'Tizita Echoes',
    amharic: 'ትዝታ',
    tagline: 'A melody of longing, played back.',
    howTo: 'The krar plays a phrase in the tizita scale. Echo it back note for note. Every round adds one more note to remember.',
    controls: 'Click / tap the strings',
    accent: '#A88BFF',
    categories: ['Music', 'Puzzle'],
  },
  {
    slug: 'axum-ascendant',
    name: 'Axum Ascendant',
    amharic: 'አክሱም',
    tagline: 'Raise a stela to the sky.',
    howTo: 'Each granite block slides across the top of your stela. Drop it dead centre to keep your width. Every misalignment shears off the overhang.',
    controls: 'Tap / Space to drop',
    accent: '#73A7FF',
    categories: ['Casual', 'Puzzle'],
  },
  {
    slug: 'gondar-castle',
    name: 'Gondar Castle',
    amharic: 'ጎንደር',
    tagline: 'Hold the walls of Fasilides.',
    howTo: 'Keep the stone in play with your rampart and break every block in the castle wall. Clear a wall and a tougher one takes its place.',
    controls: '← → or A/D to move · drag on touch',
    accent: '#FF5A6E',
    categories: ['Action', 'Casual'],
  },
  {
    slug: 'buna-ceremony',
    name: 'Buna Ceremony',
    amharic: 'ቡና',
    tagline: 'Roast, grind, brew, pour.',
    howTo: 'Four stages, each its own test of nerve. Stop the roast in the sweet spot, keep the grind rhythm, hold the brew at temperature, then fill the cup.',
    controls: 'Hold and release Space / tap',
    accent: '#E9954F',
    categories: ['Music', 'Casual'],
  },
  {
    slug: 'simien-trek',
    name: 'Simien Trek',
    amharic: 'ሰሜን',
    tagline: 'Leap the escarpment with the geladas.',
    howTo: 'Hop from ledge to ledge along the Simien cliffs. Hold to charge your jump as the gaps keep widening. There is nothing below.',
    controls: 'Hold Space / tap to charge, release to jump',
    accent: '#6FDB8C',
    categories: ['Adventure', 'Action'],
  },
  {
    slug: 'dallol-heat',
    name: 'Dallol Heat',
    amharic: 'ዳሎል',
    tagline: 'The hottest place on earth.',
    howTo: 'Sulphur vents flare where the ground glows. Step off before they blow and shelter in the shade to shed heat.',
    controls: 'WASD / arrows to move',
    accent: '#D8F044',
    categories: ['Action', 'Adventure'],
  },
  {
    slug: 'omo-valley',
    name: 'Omo Valley',
    amharic: 'ኦሞ',
    tagline: 'Paint the pattern from memory.',
    howTo: 'A body-paint pattern appears, then fades. Reproduce it by tapping the same dots in any order before the river runs dry.',
    controls: 'Click / tap the dots',
    accent: '#39D6C4',
    categories: ['Puzzle', 'Casual'],
  },
  {
    slug: 'addis-runner',
    name: 'Addis Runner',
    amharic: 'አዲስ ሩጫ',
    tagline: 'Outrun the kaiju through Addis.',
    howTo: 'A kaiju is tearing through the city and Tiki is running ahead of it. Jump the traffic, duck the low spans, and grab every coin you can reach. Each one is worth ten metres on the board. One hit ends the run.',
    controls: 'Space / ↑ to jump · Shift / ↓ to duck · P to pause',
    accent: '#C86BFF',
    categories: ['Action', 'Casual'],
  },
  {
    slug: 'addis-kick',
    name: 'Addis Kick',
    amharic: 'አዲስ ኪክ',
    tagline: 'Street football, Addis rules.',
    howTo: 'Pick a stage, aim at the target, then charge the shot and release. Stages, kits and daily challenges are all handled inside the game itself.',
    controls: 'Drag to aim · charge and release to kick',
    accent: '#FFD84D',
    categories: ['Sports', 'Casual'],
    embed: {
      url: '/games/addis-kick/index.html',
      engine: 'Unity WebGL',
      downloadMb: 70,
      studio: 'Veh Studios',
    },
  },
];

export const GAME_SLUGS = GAMES.map((g) => g.slug);

const GENRE_BY_SLUG: Record<string, string> = {
  gursha: 'Timing',
  'piassa-drift': 'Arcade',
  'lalibela-carver': 'Precision',
  'tizita-echoes': 'Memory',
  'axum-ascendant': 'Stacking',
  'gondar-castle': 'Breakout',
  'buna-ceremony': 'Rhythm',
  'simien-trek': 'Platformer',
  'dallol-heat': 'Survival',
  'omo-valley': 'Puzzle',
  'addis-runner': 'Runner',
  'addis-kick': 'Football',
};

export function gameGenre(slug: string) {
  return GENRE_BY_SLUG[slug] ?? 'Arcade';
}

export function gamesInCategory(category: GameCategory): GameMeta[] {
  return GAMES.filter((g) => g.categories.includes(category));
}

// Counts every category a game belongs to, so the totals sum to more than the
// catalog size.
export function categoryCounts(): Record<GameCategory, number> {
  const counts = Object.fromEntries(GAME_CATEGORIES.map((c) => [c, 0])) as Record<GameCategory, number>;
  for (const game of GAMES) {
    for (const category of game.categories) counts[category] += 1;
  }
  return counts;
}

export function getGame(slug: string): GameMeta | undefined {
  return GAMES.find((g) => g.slug === slug);
}
